import { defaultLocale, dynamicActivate } from './i18n-init'
import { i18n } from '@lingui/core'
import { useEffect } from 'react'
import './output.css'
import './overrides.global.css'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { Redirect, Route, Router, Switch, useLocation } from 'react-router-dom'
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react'
import { initPusher } from '@global/js/pusher'
import * as SnackbarActions from '@global/snackbar/snackbar.actions'
import { StyledEngineProvider } from '@mui/material/styles'
import { Theme } from './Theme'
import { DisabledAccountGuard } from './authed/disabled-account-guard'
import { SsoGuard } from './authed/sso-guard'
import { PageLayout } from './components/layout/pageLayout'
import { PageTitleContextProvider, SearchParamsContextProvider } from './contexts'
import { RootContext } from './contexts/root'
import { SelectiveDatasetProvider } from './datasets/create-dataset/selective/context'
import { DatasetDataContextProvider } from './datasets/dataset-details/datasetData/context'
import { ErrorCorrectionProvider } from './error-correction'
import { getTheme } from './getTheme'
import { LoadingPage } from './global/loading'
import { CVSSnackbar } from './global/snackbar/snackbar'
import { makeQueryErrorMessage } from './global/utils/reactQuery'
import { Auth } from './login/auth'
import { DomainFirstLogin } from './login/domain-first-login'
import { store } from './redux/store'

import 'regenerator-runtime'
import { LiveEnvBanner } from './components/live-env-banner'
import { customHistory } from './global/history'
import { BootstrapGuard } from './guards/BootstrapGuard'
import { ExternalLoginGuard } from './guards/external-login-guard'
import { Auth0Guard } from './guards/auth0-guard'
import { LiveblocksProvider } from '@liveblocks/react/suspense'
import './index.global.css'
import './index.css'
import { useAppSelector } from './redux/hooks'
import { FeatureDict, selectFeatureIncludes } from './authed/selectors'
import { I18nProvider } from '@lingui/react'
import { getLiveblocksAuth, getUsers } from './global/api/liveblocks'
import PropTypes from 'prop-types'

const PrivateRoute = ({ component: Component, ...rest }) => {
  PrivateRoute.propTypes = {
    component: PropTypes.elementType.isRequired,
  }

  const location = useLocation()
  const loggedIn = !!useSelector(state => state.Auth.user)
  const loading = !!useSelector(state => state.Auth.loading)

  if (loading) {
    return <LoadingPage />
  }

  if (!loggedIn) {
    const loginDestination = {
      pathname: '/auth/login',
      search: location.search, // keep ?login=system if it exists to prevent infinite loop with sso
      state: { referrer: location },
    }

    return <Redirect to={loginDestination} />
  }

  return <Route {...rest} component={Component} />
}

const GuestRoute = ({ component: Component, ...rest }) => {
  GuestRoute.propTypes = {
    component: PropTypes.elementType.isRequired,
  }

  const loading = !!useSelector(state => state.Auth.loading)

  if (loading) {
    return <LoadingPage />
  }

  return <Route {...rest} component={Component} />
}

export const AppWrapper = () => {
  useEffect(() => {
    dynamicActivate(defaultLocale)
  }, [])

  return (
    <I18nProvider i18n={i18n}>
      <Provider store={store}>
        <App />
      </Provider>
    </I18nProvider>
  )
}

const AppContent = () => {
  const [ENABLE_LIVEBLOCKS = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.ENABLE_LIVEBLOCKS]),
  )

  const switchContent = (
    <Switch>
      <GuestRoute path="/auth" component={Auth} />
      <SearchParamsContextProvider>
        <PrivateRoute path="/" component={PageLayout} />
      </SearchParamsContextProvider>
    </Switch>
  )

  return ENABLE_LIVEBLOCKS ? (
    <LiveblocksProvider
      authEndpoint={getLiveblocksAuth}
      resolveUsers={({ userIds }) => getUsers(userIds)}
    >
      {switchContent}
    </LiveblocksProvider>
  ) : (
    switchContent
  )
}

const App = () => {
  const dispatch = useDispatch()
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, _query) => {
        // TODO: we need global unit tests to ensure error catching behavior is working as expected
        const message = makeQueryErrorMessage(error)
        if (message) {
          dispatch(SnackbarActions.showSnackbar({ type: 'ERROR', message }))
        }
      },
    }),
  })

  // if at the login domain, then hijack everything
  const {
    location: { hostname },
  } = window
  const parts = hostname.split('.')
  const account = parts[0]
  const useMiniLogin = account === 'login' || account === 'app'

  useEffect(() => {
    initPusher({
      appKey: window.PUSHER_APP_KEY,
      cluster: window.PUSHER_APP_CLUSTER,
      forceTLS: window.PUSHER_APP_FORCE_TLS === 'true',
    })
  }, [])

  if (useMiniLogin) {
    return (
      <Theme>
        <Router history={customHistory}>
          <Route path="/" component={DomainFirstLogin} />
        </Router>
      </Theme>
    )
  }

  return (
    <EmotionThemeProvider theme={getTheme()}>
      <StyledEngineProvider injectFirst>
        <Theme>
          <RootContext>
            <QueryClientProvider client={queryClient}>
              <ReactQueryDevtools initialIsOpen={false} position="bottom-left" />
              <Auth0Guard>
                <BootstrapGuard>
                  <ExternalLoginGuard>
                    <SsoGuard>
                      <Router history={customHistory}>
                        <DatasetDataContextProvider>
                          <SelectiveDatasetProvider>
                            <ErrorCorrectionProvider>
                              <PageTitleContextProvider>
                                <DisabledAccountGuard>
                                  <LiveEnvBanner />
                                  <AppContent />
                                </DisabledAccountGuard>
                              </PageTitleContextProvider>
                            </ErrorCorrectionProvider>
                          </SelectiveDatasetProvider>
                        </DatasetDataContextProvider>
                      </Router>
                    </SsoGuard>
                  </ExternalLoginGuard>
                </BootstrapGuard>
              </Auth0Guard>
            </QueryClientProvider>
            <CVSSnackbar />
          </RootContext>
        </Theme>
      </StyledEngineProvider>
    </EmotionThemeProvider>
  )
}
